import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useGlobalStore } from "@/GlobalStore";
import {
  fetchActiveOAuthConnections,
  OAuthConnectionJob,
} from "@/api/globalApiService";
import useAccountsList from "./useAccountsList";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import ToastCompact from "@/components/common/toast/ToastCompact";
import { ClickableToast } from "@/components/common/toast/ClickableToast";

const OAUTH_STATUS_PARAM = "oauth_status";
const OAUTH_PLATFORM_PARAM = "oauth_platform";
const OAUTH_JOB_ID_PARAM = "oauth_job_id";
const POLLING_INTERVAL_MS = 4000;

const PLATFORM_LABELS: Record<string, string> = {
  GOOGLE: "Google",
  FACEBOOK: "Meta",
  TIKTOK: "TikTok",
  LINKEDIN: "LinkedIn",
  MICROSOFT: "Microsoft",
};

type PendingJob = {
  jobId: string;
  platformKey: string | null;
  platformLabel: string;
};

const getPlatformLabel = (platform?: string | null) => {
  if (!platform) return "Ad";
  return PLATFORM_LABELS[platform] ?? platform;
};

const toPendingJobMap = (jobs: OAuthConnectionJob[]): Map<string, PendingJob> => {
  const map = new Map<string, PendingJob>();
  jobs.forEach((job) => {
    const platformKey = (job.platform as string | undefined) ?? null;
    map.set(job.job_id, {
      jobId: job.job_id,
      platformKey,
      platformLabel: getPlatformLabel(platformKey),
    });
  });
  return map;
};

export const useOauthConnectionStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  useAccountsList();

  const noAccountsAvailable = useGlobalStore((state) => state.noAccountsAvailable);
  const isOauthConnecting = useGlobalStore((state) => state.isOauthConnecting);
  const oauthConnectingPlatformLabel = useGlobalStore(
    (state) => state.oauthConnectingPlatformLabel,
  );
  const setOauthConnectingState = useGlobalStore(
    (state) => state.setOauthConnectingState,
  );

  const isMountedRef = useRef(true);
  const pollingTimeoutRef = useRef<number | null>(null);
  const activeJobsRef = useRef<Map<string, PendingJob>>(new Map());
  const loaderToastByJobRef = useRef<Map<string, string | number>>(new Map());
  const pollingInFlightRef = useRef(false);

  const clearPolling = () => {
    if (pollingTimeoutRef.current !== null) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  const clearOAuthQueryParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete(OAUTH_STATUS_PARAM);
    params.delete(OAUTH_PLATFORM_PARAM);
    params.delete(OAUTH_JOB_ID_PARAM);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const syncGlobalConnectingState = useCallback(() => {
    const jobs = Array.from(activeJobsRef.current.values());
    if (jobs.length === 0) {
      setOauthConnectingState({ isConnecting: false, platformLabel: null });
      return;
    }
    const label = jobs.length === 1 ? jobs[0].platformLabel : "Multiple";
    setOauthConnectingState({ isConnecting: true, platformLabel: label });
  }, [setOauthConnectingState]);

  const syncLoaderToasts = useCallback(
    (jobsMap: Map<string, PendingJob>) => {
      if (noAccountsAvailable) {
        for (const id of loaderToastByJobRef.current.values()) {
          toast.dismiss(id);
        }
        loaderToastByJobRef.current.clear();
        return;
      }

      for (const [jobId, job] of jobsMap.entries()) {
        if (loaderToastByJobRef.current.has(jobId)) continue;
        const id = toast.custom(
          (t) => (
            <ToastCompact
              messagePrefix="Connecting"
              messageHighlight={job.platformLabel}
              messageSuffix="Accounts"
              isLoading
              onDismiss={() => toast.dismiss(t)}
            />
          ),
          { duration: Infinity },
        );
        loaderToastByJobRef.current.set(jobId, id);
      }

      for (const [jobId, id] of Array.from(loaderToastByJobRef.current.entries())) {
        if (!jobsMap.has(jobId)) {
          toast.dismiss(id);
          loaderToastByJobRef.current.delete(jobId);
        }
      }
    },
    [noAccountsAvailable],
  );

  const showSuccessToastWithCTA = useCallback(
    (job: PendingJob) => {
      toast.custom(
        (t) => (
          <ClickableToast
            messagePrefix="Account connection successful"
            descriptionPrefix="Show connected accounts"
            hasDescription
            onDismiss={() => toast.dismiss(t)}
            onClick={() => {
              if (job.platformKey) {
                const params = new URLSearchParams(window.location.search);
                params.set(OAUTH_PLATFORM_PARAM, job.platformKey);
                setSearchParams(params, { replace: true });
              }
              toast.dismiss(t);
            }}
          />
        ),
        { duration: Infinity },
      );
    },
    [setSearchParams],
  );

  const applyJobs = useCallback(
    async (nextJobs: Map<string, PendingJob>) => {
      const prevJobs = activeJobsRef.current;
      const completed = Array.from(prevJobs.values()).filter((j) => !nextJobs.has(j.jobId));

      activeJobsRef.current = nextJobs;
      syncGlobalConnectingState();
      syncLoaderToasts(nextJobs);

      if (completed.length > 0) {
        completed.forEach(showSuccessToastWithCTA);
      }
    },
    [showSuccessToastWithCTA, syncGlobalConnectingState, syncLoaderToasts],
  );

  const poll = useCallback(async () => {
    if (!isMountedRef.current || pollingInFlightRef.current) return;
    pollingInFlightRef.current = true;

    try {
      const jobs = await fetchActiveOAuthConnections();
      if (!isMountedRef.current) return;
      await applyJobs(toPendingJobMap(jobs));
    } catch (error) {
      console.error("Error polling active OAuth connections:", error);
    } finally {
      pollingInFlightRef.current = false;
    }

    if (!isMountedRef.current || activeJobsRef.current.size === 0) {
      clearPolling();
      return;
    }

    clearPolling();
    pollingTimeoutRef.current = window.setTimeout(() => {
      void poll();
    }, POLLING_INTERVAL_MS);
  }, [applyJobs]);

  const startPolling = useCallback(() => {
    clearPolling();
    void poll();
  }, [poll]);

  useEffect(() => {
    isMountedRef.current = true;

    const status = searchParams.get(OAUTH_STATUS_PARAM);
    const platform = searchParams.get(OAUTH_PLATFORM_PARAM);
    const jobId = searchParams.get(OAUTH_JOB_ID_PARAM);

    if (status === "failed") {
      clearOAuthQueryParams();
      setOauthConnectingState({ isConnecting: false, platformLabel: null });
      toast.message(
        <ToastContent
          headingPrefix="Failed to connect accounts"
          subheadingPrefix="Authentication failed. Please try again."
          isError
        />,
      );
    } else if (status === "success") {
      const terminalJob: PendingJob = {
        jobId: "terminal-success",
        platformKey: platform,
        platformLabel: getPlatformLabel(platform),
      };
      clearOAuthQueryParams();
      showSuccessToastWithCTA(terminalJob);
    } else if (status === "pending" && jobId) {
      const pendingMap = new Map<string, PendingJob>();
      pendingMap.set(jobId, {
        jobId,
        platformKey: platform,
        platformLabel: getPlatformLabel(platform),
      });
      clearOAuthQueryParams();
      void applyJobs(pendingMap);
      startPolling();
    } else if (!status) {
      // Recovery path when URL has no oauth params.
      void (async () => {
        try {
          const jobs = await fetchActiveOAuthConnections();
          if (!isMountedRef.current) return;
          const pendingMap = toPendingJobMap(jobs);
          await applyJobs(pendingMap);
          if (pendingMap.size > 0) {
            startPolling();
          }
        } catch (error) {
          console.error("Error recovering active OAuth connections:", error);
        }
      })();
    }

    return () => {
      isMountedRef.current = false;
      clearPolling();
      for (const id of loaderToastByJobRef.current.values()) {
        toast.dismiss(id);
      }
      loaderToastByJobRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOauthConnecting,
    oauthConnectingPlatformLabel,
  };
};

export default useOauthConnectionStatus;
